import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import { ROLES } from '../../../constants';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';
import Badge from '../../../shared/Badge';

const ROLE_LABELS = {
  [ROLES.SUPERADMIN]: 'Superadmin',
  [ROLES.ADMIN_COMPANY]: 'Admin Company',
  [ROLES.EMPLOYEE]: 'Employee Company',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Nonaktif' },
];

const normalizeCompanyId = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getDisplayWilayah = (wilayah) => {
  if (typeof wilayah !== 'string') return '-';
  const trimmed = wilayah.trim();
  return trimmed || '-';
};

const normalizeTextFilter = (value) => String(value || '').trim();
const normalizePage = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return parsed;
};
const normalizeRoleFilter = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if ([ROLES.SUPERADMIN, ROLES.ADMIN_COMPANY, ROLES.EMPLOYEE].includes(raw)) return raw;
  return '';
};

const UsersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: loggedInUser } = useAuth();

  const isSuperadmin = loggedInUser?.role === ROLES.SUPERADMIN;
  const isAdminCompany = loggedInUser?.role === ROLES.ADMIN_COMPANY;
  const loggedInCompanyId = normalizeCompanyId(loggedInUser?.company_id);
  const loggedInUserId = Number(loggedInUser?.user_id);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const fetchRequestIdRef = useRef(0);

  const [toast, setToast] = useState('');
  const toastTimerRef = useRef(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: ROLES.ADMIN_COMPANY,
    company_id: '',
    status: 'active',
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'active',
  });

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });
  const roleFilter = normalizeRoleFilter(searchParams.get('role'));
  const searchFilter = normalizeTextFilter(searchParams.get('search'));
  const listPage = normalizePage(searchParams.get('page'));

  const showToast = (message) => {
    if (!message) return;
    setToast(message);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToast('');
      toastTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
  }, []);

  const getTargetCompanyId = (target) => normalizeCompanyId(target?.company_id ?? target?.company?.company_id);

  const canManageRole = (role) => role === ROLES.SUPERADMIN || role === ROLES.ADMIN_COMPANY;

  const canCreateRole = (role) => {
    if (isSuperadmin) return role === ROLES.SUPERADMIN || role === ROLES.ADMIN_COMPANY;
    if (isAdminCompany) return role === ROLES.ADMIN_COMPANY;
    return false;
  };

  const canEditTarget = (target) => {
    if (!target || !canManageRole(target.role)) return false;
    if (isSuperadmin) return true;
    if (isAdminCompany) {
      return target.role === ROLES.ADMIN_COMPANY && getTargetCompanyId(target) === loggedInCompanyId;
    }
    return false;
  };

  const canDeactivateTarget = (target) => canEditTarget(target);

  const canChangePasswordTarget = (target) => {
    if (!target || !canManageRole(target.role)) return false;
    if (isSuperadmin) return true;
    if (isAdminCompany) {
      return Number(target.user_id) === loggedInUserId;
    }
    return false;
  };

  const fetchUsers = async ({ page = listPage, role = roleFilter, search = searchFilter } = {}) => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    const normalizedPage = normalizePage(page);
    const normalizedRole = normalizeRoleFilter(role);
    const normalizedSearch = normalizeTextFilter(search);

    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const res = await adminService.getUsers({
        page: normalizedPage,
        limit: 10,
        role: normalizedRole || undefined,
        search: normalizedSearch || undefined,
      });
      if (requestId !== fetchRequestIdRef.current) return;

      const list = extractList(res.data);
      const meta = extractMeta(res.data);
      setUsers(list);
      setPagination(meta);
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;

      setUsers([]);
      setPagination({ page: 1, last_page: 1, total: 0 });
      showToast(err?.message || 'Gagal memuat data user');
    } finally {
      if (requestId !== fetchRequestIdRef.current) return;

      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  };

  const fetchCompanies = async () => {
    if (!isSuperadmin) return;
    try {
      const res = await adminService.getCompanies({ page: 1, limit: 1000 });
      setCompanies(extractList(res.data));
    } catch (err) {
      setCompanies([]);
      showToast(err?.message || 'Gagal memuat daftar perusahaan');
    }
  };

  useEffect(() => {
    fetchUsers({ page: listPage, role: roleFilter, search: searchFilter });
  }, [listPage, roleFilter, searchFilter]);

  useEffect(() => {
    if (isSuperadmin) {
      fetchCompanies();
    }
  }, [isSuperadmin]);

  const updateListSearchParams = ({
    roleValue = roleFilter,
    searchValue = searchFilter,
    pageValue = listPage,
  } = {}) => {
    const next = new URLSearchParams(searchParams);

    const normalizedRole = normalizeRoleFilter(roleValue);
    if (normalizedRole) next.set('role', normalizedRole);
    else next.delete('role');

    const normalizedSearch = normalizeTextFilter(searchValue);
    if (normalizedSearch) next.set('search', normalizedSearch);
    else next.delete('search');

    const normalizedPage = normalizePage(pageValue);
    if (normalizedPage > 1) next.set('page', String(normalizedPage));
    else next.delete('page');

    setSearchParams(next);
  };

  const handleRoleFilterChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      roleValue: value,
      pageValue: 1,
    });
  };

  const handleSearchFilterChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      searchValue: value,
      pageValue: 1,
    });
  };

  const openCreateModal = () => {
    if (!isSuperadmin && !isAdminCompany) {
      showToast('Anda tidak memiliki akses untuk menambah user');
      return;
    }

    setCreateForm({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: ROLES.ADMIN_COMPANY,
      company_id: isSuperadmin ? '' : String(loggedInCompanyId || ''),
      status: 'active',
    });
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setCreateModalOpen(false);
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();

    if (!createForm.name.trim() || !createForm.email.trim() || !createForm.password) {
      showToast('Nama, email, dan password wajib diisi');
      return;
    }

    if (!canCreateRole(createForm.role)) {
      showToast('Role user tidak diizinkan');
      return;
    }

    const payload = {
      name: createForm.name.trim(),
      email: createForm.email.trim().toLowerCase(),
      password: createForm.password,
      role: createForm.role,
      status: createForm.status,
    };

    if (createForm.phone.trim()) {
      payload.phone = createForm.phone.trim();
    }

    if (createForm.role === ROLES.ADMIN_COMPANY) {
      const companyId = isSuperadmin
        ? normalizeCompanyId(createForm.company_id)
        : loggedInCompanyId;

      if (!companyId) {
        showToast('Company harus dipilih untuk role Admin Company');
        return;
      }

      payload.company_id = companyId;
    }

    setCreateSubmitting(true);
    try {
      const res = await adminService.createUser(payload);
      showToast(res?.message || 'User berhasil dibuat');
      setCreateModalOpen(false);
      await fetchUsers({ page: listPage, role: roleFilter, search: searchFilter });
    } catch (err) {
      showToast(err?.message || 'Gagal membuat user');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openEditModal = async (target) => {
    if (!canEditTarget(target)) {
      showToast('Anda tidak memiliki akses untuk mengedit user ini');
      return;
    }

    setEditModalOpen(true);
    setEditLoading(true);

    try {
      const res = await adminService.getUserById(target.user_id);
      const detail = res.data;

      if (!canEditTarget(detail)) {
        throw new Error('User ini tidak dapat diedit');
      }

      setEditingUser(detail);
      setEditForm({
        name: detail.name || '',
        email: detail.email || '',
        phone: detail.phone || '',
        status: detail.status || 'active',
      });
    } catch (err) {
      setEditModalOpen(false);
      setEditingUser(null);
      showToast(err?.message || 'Gagal memuat detail user');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (editSubmitting) return;
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!editingUser) return;

    if (!editForm.name.trim() || !editForm.email.trim()) {
      showToast('Nama dan email wajib diisi');
      return;
    }

    if (!canEditTarget(editingUser)) {
      showToast('Anda tidak memiliki akses untuk mengedit user ini');
      return;
    }

    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.trim().toLowerCase(),
      phone: editForm.phone.trim(),
      status: editForm.status,
    };

    setEditSubmitting(true);
    try {
      const res = await adminService.updateUser(editingUser.user_id, payload);
      showToast(res?.message || 'User berhasil diperbarui');
      setEditModalOpen(false);
      setEditingUser(null);
      await fetchUsers({ page: listPage, role: roleFilter, search: searchFilter });
    } catch (err) {
      showToast(err?.message || 'Gagal memperbarui user');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeactivate = async (target) => {
    if (!canDeactivateTarget(target)) {
      showToast('Anda tidak memiliki akses untuk menonaktifkan user ini');
      return;
    }

    if (target.status === 'inactive') {
      showToast('User sudah nonaktif');
      return;
    }

    const confirmed = window.confirm(`Nonaktifkan user ${target.name}?`);
    if (!confirmed) return;

    try {
      const res = await adminService.updateUser(target.user_id, { status: 'inactive' });
      showToast(res?.message || 'User berhasil dinonaktifkan');
      await fetchUsers({ page: listPage, role: roleFilter, search: searchFilter });
    } catch (err) {
      showToast(err?.message || 'Gagal menonaktifkan user');
    }
  };

  const openPasswordModal = (target) => {
    if (!canChangePasswordTarget(target)) {
      showToast('Anda tidak memiliki akses untuk mengganti password user ini');
      return;
    }

    setPasswordTarget(target);
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    if (passwordSubmitting) return;
    setPasswordModalOpen(false);
    setPasswordTarget(null);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!passwordTarget) return;

    if (!passwordForm.password || !passwordForm.confirmPassword) {
      showToast('Password dan konfirmasi password wajib diisi');
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      showToast('Konfirmasi password tidak sama');
      return;
    }

    if (!canChangePasswordTarget(passwordTarget)) {
      showToast('Anda tidak memiliki akses untuk mengganti password user ini');
      return;
    }

    setPasswordSubmitting(true);
    try {
      const res = await adminService.updateUser(passwordTarget.user_id, {
        password: passwordForm.password,
      });
      showToast(res?.message || 'Password berhasil diperbarui');
      setPasswordModalOpen(false);
      setPasswordTarget(null);
    } catch (err) {
      showToast(err?.message || 'Gagal memperbarui password');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const companyNameById = (companyId) => {
    const normalizedId = normalizeCompanyId(companyId);
    if (!normalizedId) return '-';
    return companies.find((item) => normalizeCompanyId(item.company_id) === normalizedId)?.name || '-';
  };

  if (loading && !hasLoadedOnceRef.current) return <Loading />;

  return (
    <div>
      {toast && <div className="toast">{toast}</div>}

      <div className="admin-users-toolbar">
        <div className="admin-table-controls">
          <label htmlFor="users-role-filter">Filter Role</label>
          <select
            id="users-role-filter"
            className="admin-filter-select"
            value={roleFilter}
            onChange={handleRoleFilterChange}
          >
            <option value="">Semua Role</option>
            <option value={ROLES.SUPERADMIN}>{ROLE_LABELS[ROLES.SUPERADMIN]}</option>
            <option value={ROLES.ADMIN_COMPANY}>{ROLE_LABELS[ROLES.ADMIN_COMPANY]}</option>
            <option value={ROLES.EMPLOYEE}>{ROLE_LABELS[ROLES.EMPLOYEE]}</option>
          </select>

          <label htmlFor="users-search-filter">Search</label>
          <input
            id="users-search-filter"
            type="text"
            className="admin-filter-select"
            value={searchFilter}
            onChange={handleSearchFilterChange}
            placeholder="Cari nama, email, atau phone"
          />
        </div>

        {(isSuperadmin || isAdminCompany) && (
          <button type="button" className="admin-btn-primary" onClick={openCreateModal}>
            + Tambah User
          </button>
        )}
      </div>

      <p className="admin-page__meta">Total: {pagination.total} user</p>

      {users.length === 0 ? (
        <EmptyState icon="USER" title="Tidak ada user" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Email</th>
                <th>Role</th>
                <th>Company</th>
                <th>Wilayah</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((target) => (
                <tr key={target.user_id}>
                  <td>{target.name}</td>
                  <td>{target.email}</td>
                  <td><span className="admin-role-tag">{target.role}</span></td>
                  <td className="admin-company-name">{target.company?.name || companyNameById(target.company_id)}</td>
                  <td>{getDisplayWilayah(target.wilayah)}</td>
                  <td><Badge status={target.status} /></td>
                  <td>
                    <div className="admin-user-actions">
                      <button
                        type="button"
                        className="admin-btn-link"
                        onClick={() => navigate(`/admin/users/${target.user_id}`)}
                      >
                        Lihat
                      </button>

                      {canEditTarget(target) && (
                        <button
                          type="button"
                          className="admin-user-action"
                          onClick={() => openEditModal(target)}
                        >
                          Edit
                        </button>
                      )}

                      {canDeactivateTarget(target) && (
                        <button
                          type="button"
                          className="admin-user-action admin-user-action--danger"
                          onClick={() => handleDeactivate(target)}
                          disabled={target.status === 'inactive'}
                        >
                          Nonaktifkan
                        </button>
                      )}

                      {canChangePasswordTarget(target) && (
                        <button
                          type="button"
                          className="admin-user-action admin-user-action--warning"
                          onClick={() => openPasswordModal(target)}
                        >
                          Ganti Password
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="admin-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => updateListSearchParams({ pageValue: listPage - 1 })}
          >
            {'< Prev'}
          </button>
          <span>Hal {pagination.page} / {pagination.last_page}</span>
          <button
            type="button"
            disabled={pagination.page >= pagination.last_page}
            onClick={() => updateListSearchParams({ pageValue: listPage + 1 })}
          >
            {'Next >'}
          </button>
        </div>
      )}

      {createModalOpen && (
        <div className="admin-modal-overlay" onClick={closeCreateModal}>
          <div className="admin-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Tambah User</h3>
              <button type="button" className="admin-modal-close" onClick={closeCreateModal}>x</button>
            </div>

            <form className="admin-modal-form" onSubmit={handleCreateSubmit}>
              <div className="admin-modal-grid">
                <div className="admin-modal-field">
                  <label>Nama *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                    required
                  />
                </div>

                <div className="admin-modal-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
                    required
                  />
                </div>

                <div className="admin-modal-field">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={createForm.phone}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, phone: event.target.value }))}
                  />
                </div>

                <div className="admin-modal-field">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, password: event.target.value }))}
                    required
                  />
                </div>

                {isSuperadmin ? (
                  <div className="admin-modal-field">
                    <label>Role *</label>
                    <select
                      value={createForm.role}
                      onChange={(event) => {
                        const nextRole = event.target.value;
                        setCreateForm((prev) => ({
                          ...prev,
                          role: nextRole,
                          company_id: nextRole === ROLES.ADMIN_COMPANY ? prev.company_id : '',
                        }));
                      }}
                    >
                      <option value={ROLES.ADMIN_COMPANY}>{ROLE_LABELS[ROLES.ADMIN_COMPANY]}</option>
                      <option value={ROLES.SUPERADMIN}>{ROLE_LABELS[ROLES.SUPERADMIN]}</option>
                    </select>
                  </div>
                ) : (
                  <div className="admin-modal-field">
                    <label>Role</label>
                    <input type="text" value={ROLE_LABELS[ROLES.ADMIN_COMPANY]} disabled />
                  </div>
                )}

                <div className="admin-modal-field">
                  <label>Status</label>
                  <select
                    value={createForm.status}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, status: event.target.value }))}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {createForm.role === ROLES.ADMIN_COMPANY && (
                  isSuperadmin ? (
                    <div className="admin-modal-field admin-modal-field--full">
                      <label>Company *</label>
                      <select
                        value={createForm.company_id}
                        onChange={(event) => setCreateForm((prev) => ({ ...prev, company_id: event.target.value }))}
                        required
                      >
                        <option value="">Pilih Company</option>
                        {companies.map((company) => (
                          <option key={company.company_id} value={company.company_id}>{company.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="admin-modal-field admin-modal-field--full">
                      <label>Company</label>
                      <input type="text" value={loggedInUser?.company?.name || 'Company sendiri'} disabled />
                    </div>
                  )
                )}
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={closeCreateModal} disabled={createSubmitting}>
                  Batal
                </button>
                <button type="submit" className="admin-btn-primary" disabled={createSubmitting}>
                  {createSubmitting ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="admin-modal-overlay" onClick={closeEditModal}>
          <div className="admin-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User</h3>
              <button type="button" className="admin-modal-close" onClick={closeEditModal}>x</button>
            </div>

            {editLoading ? (
              <div className="admin-modal-loading">Memuat detail user...</div>
            ) : (
              <form className="admin-modal-form" onSubmit={handleEditSubmit}>
                <div className="admin-modal-grid">
                  <div className="admin-modal-field">
                    <label>Nama *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                      required
                    />
                  </div>

                  <div className="admin-modal-field">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, email: event.target.value }))}
                      required
                    />
                  </div>

                  <div className="admin-modal-field">
                    <label>Phone</label>
                    <input
                      type="text"
                      value={editForm.phone}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, phone: event.target.value }))}
                    />
                  </div>

                  <div className="admin-modal-field">
                    <label>Status</label>
                    <select
                      value={editForm.status}
                      onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Role</label>
                    <input type="text" value={ROLE_LABELS[editingUser?.role] || editingUser?.role || '-'} disabled />
                  </div>

                  <div className="admin-modal-field">
                    <label>Company</label>
                    <input
                      type="text"
                      value={editingUser?.company?.name || companyNameById(editingUser?.company_id)}
                      disabled
                    />
                  </div>
                </div>

                <div className="admin-modal-actions">
                  <button type="button" className="admin-btn-secondary" onClick={closeEditModal} disabled={editSubmitting}>
                    Batal
                  </button>
                  <button type="submit" className="admin-btn-primary" disabled={editSubmitting}>
                    {editSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {passwordModalOpen && (
        <div className="admin-modal-overlay" onClick={closePasswordModal}>
          <div className="admin-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Ganti Password</h3>
              <button type="button" className="admin-modal-close" onClick={closePasswordModal}>x</button>
            </div>

            <form className="admin-modal-form" onSubmit={handlePasswordSubmit}>
              <p className="admin-help-text">
                Target user: <strong>{passwordTarget?.name}</strong> ({ROLE_LABELS[passwordTarget?.role] || passwordTarget?.role})
              </p>

              <div className="admin-modal-grid">
                <div className="admin-modal-field admin-modal-field--full">
                  <label>Password Baru *</label>
                  <input
                    type="password"
                    value={passwordForm.password}
                    onChange={(event) => setPasswordForm((prev) => ({ ...prev, password: event.target.value }))}
                    required
                  />
                </div>

                <div className="admin-modal-field admin-modal-field--full">
                  <label>Konfirmasi Password *</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={closePasswordModal} disabled={passwordSubmitting}>
                  Batal
                </button>
                <button type="submit" className="admin-btn-primary" disabled={passwordSubmitting}>
                  {passwordSubmitting ? 'Menyimpan...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
